import axios, { AxiosResponse } from 'axios';
import { EntityType, UploadFileStatus } from '../../types/mold';
import { FileResponse, ImageMetadata } from '../../types/moldOrder';
import { isHeadlessPrintRoute } from '../../utils/printRoute';
import api from './client';

function deleteErrorResult(error: unknown): { isSuccess: false; errorMessage: string } {
  const errorMessage = axios.isAxiosError(error)
    ? error.response?.data?.message || 'An error occurred while deleting the file'
    : 'An error occurred while deleting the file';
  console.error(
    'Error deleting file:',
    axios.isAxiosError(error) ? error.response?.data : error,
  );
  return { isSuccess: false, errorMessage };
}

export const fileService = {
  uploadFiles: async (
    entityType: EntityType,
    entityId: number,
    files: File[],
    metadata?: Record<string, any>,
  ): Promise<AxiosResponse<UploadFileStatus[]>> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    if (metadata) {
      formData.append('metadata', JSON.stringify(metadata));
    }

    return api.post(`/files/${entityType}/${entityId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
      },
    });
  },

  getFiles: async (
    entityType: EntityType,
    entityId: number,
  ): Promise<AxiosResponse<FileResponse[]>> => {
    return api.get(`/files/${entityType}/${entityId}`);
  },

  updateMetadataFile: async (
    fileName: string,
    metadata: ImageMetadata,
  ): Promise<FileResponse> => {
    return api.patch(`/files/name/${fileName}/meta`, metadata);
  },

  addTagToFiles: async (
    fileNames: string[],
    metadata: ImageMetadata,
  ): Promise<FileResponse[]> => {
    const promises = fileNames.map((fileName) =>
      fileService.updateMetadataFile(fileName, metadata),
    );
    return Promise.all(promises);
  },

  downloadFile: async (fileName: string): Promise<AxiosResponse<Blob>> => {
    // On the headless PDF print route, request a downscaled image so the browser
    // decodes a small JPEG instead of a full-resolution (~12MP) photo. Decoding many
    // full-res images at once blows the headless container's 384M memory cap, so
    // image-heavy orders 500 otherwise (the pre-redesign Go report downscaled to
    // 400px for exactly this reason). The interactive app (any non-/print/ route)
    // keeps serving full-resolution images, so this changes nothing outside print.
    const PRINT_IMAGE_MAX_HEIGHT = 800;
    const isPrintRoute = isHeadlessPrintRoute();
    return api.get(`/files/name/${fileName}`, {
      responseType: 'blob',
      ...(isPrintRoute
        ? { params: { maxHeight: PRINT_IMAGE_MAX_HEIGHT } }
        : {}),
    });
  },

  deleteFile: async (
    fileName: string,
  ): Promise<{ isSuccess: boolean; errorMessage?: string }> => {
    try {
      await api.delete(`/files/name/${fileName}`);
      return { isSuccess: true };
    } catch (error) {
      return deleteErrorResult(error);
    }
  },

  deleteImagesByTag: async (
    tag: string,
  ): Promise<{ isSuccess: boolean; errorMessage?: string }> => {
    try {
      await api.delete(`/files/tag/${tag}`);
      return { isSuccess: true };
    } catch (error) {
      return deleteErrorResult(error);
    }
  },
};
